import { useEffect, useMemo, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useCollectionBinding,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Types & helpers
// ============================================================================

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

/** A bedroom category derived from the floorplans collection. */
interface BedroomCategory {
  key: string;
  label: string;
  param: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

/** Map a raw bedroom count ("0", "2", "studio", …) to a category key. */
function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  return BED_ORDER[num] || `${num}`;
}

/** Human-readable label for a category key. */
function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

/** Group the floorplans collection into ordered bedroom categories. */
function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const keys = new Set<string>();
  for (const plan of floorplans) {
    keys.add(bedCountToKey(plan.floorplan_beds_min));
  }

  return Array.from(keys)
    .sort((a, b) => {
      const ai = BED_ORDER.indexOf(a);
      const bi = BED_ORDER.indexOf(b);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    })
    .map((key) => ({ key, label: keyToLabel(key), param: key }));
}

// Shown on the company theme (and any property without floor plans) so the
// section always renders the four representative unit types.
const FALLBACK_CATEGORIES: BedroomCategory[] = [
  { key: 'studio', label: 'Studio', param: 'studio' },
  { key: 'one', label: '1 Bedroom', param: 'one' },
  { key: 'two', label: '2 Bedroom', param: 'two' },
  { key: 'three', label: '3 Bedroom', param: 'three' },
];

/** Derive the circular badge label (S / 1 / 2 / …) from a category key. */
function badgeForKey(key: string, index: number): string {
  if (key === 'studio') return 'S';
  const ordered = BED_ORDER.indexOf(key);
  if (ordered > 0) return String(ordered);
  const numeric = key.match(/\d+/);
  if (numeric) return numeric[0];
  return String(index + 1);
}

/** Abstract line-art floor plan glyph, open at the top-right for the badge. */
function FloorPlanIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 120 108"
      fill="none"
      stroke="currentColor"
      strokeWidth="6"
      strokeLinecap="square"
      strokeLinejoin="miter"
      className={className}
      aria-hidden="true"
    >
      {/* Outer walls — open at the top-right corner where the badge sits */}
      <polyline points="80,6 6,6 6,102 114,102 114,52" />
      {/* Top-left room */}
      <polyline points="6,42 46,42 46,6" />
      {/* Central dividing walls */}
      <polyline points="46,42 46,70 84,70 84,102" />
      {/* Right room wall */}
      <line x1="114" y1="70" x2="84" y2="70" />
    </svg>
  );
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const featureImageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a furnished apartment interior photo. Prefer gallery photos whose title, alt, description, or filename match cues like: interior, apartment, unit, living, kitchen, bedroom, home, modern, luxury, inside, hallway, dining — and weight furnished/staged cues higher (furnished, staged, model, sofa, couch, bed, decor, furniture). Skip photos matching: floor plan, floorplan, site map, sitemap, diagram, blueprint, empty, vacant, unfurnished, no furniture, move-in ready. Do not use bathrooms, laundry rooms, or closets. Avoid exterior shots and amenity/common-area photos.',
    context:
      'Feature image beside the floor-plan copy in the Floor Plans section on the home page; it should show a primary living space inside a unit.',
  },
};

const featureCtaDef: SlotGroupDefinition = {
  label: 'Feature CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floorplans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Now' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Scroll reveal — slide elements up into view as they enter the viewport
// ============================================================================

/**
 * Observes descendants marked with `data-reveal` and toggles the `is-visible`
 * class as each scrolls into view (one-shot). Falls back to showing everything
 * immediately when IntersectionObserver is unavailable (e.g. during SSG) or the
 * user prefers reduced motion.
 */
function useScrollReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const els = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (!els.length) return;

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return ref;
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const revealRef = useScrollReveal<HTMLDivElement>();
  const image = useContentToken('floorplanslist.section_image');
  const featureImage = useSlotGroup('floorplanslist.feature_image', featureImageDef);
  const featureCta = useSlotGroup('floorplanslist.feature_cta', featureCtaDef);

  // Derive bedroom categories from the floorplans collection (grouped by bed
  // count), falling back to representative unit types when none are present.
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });
  const categories = useMemo(() => {
    const grouped = floorplans?.length ? groupFloorplansByBeds(floorplans) : [];
    return (grouped.length > 0 ? grouped : FALLBACK_CATEGORIES).slice(0, 4);
  }, [floorplans]);

  return (
    <section className="relative overflow-hidden bg-[var(--vibe-color-background)] min-h-[720px] md:min-h-[1100px]">
      {/* Full-section property image, clipped to an angled top edge */}
      <div className="fp-section-image absolute inset-0">
        <EditableToken
          path="floorplanslist.section_image"
          type="image"
          as="div"
          label="Section Image"
          className="w-full h-full"
        >
          {image ? (
            <VibesitesImage
              url={image}
              alt="Property"
              sizes="100vw"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          ) : (
            <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
          )}
        </EditableToken>
        {/* 77% primary-color overlay */}
        <div className="absolute inset-0 bg-[var(--vibe-color-primary)] opacity-[0.77]" />
      </div>

      {/* Floor plan type CTAs + feature block overlaid on top of the image section.
          justify-end bottom-anchors both groups so the feature block's interior image
          keeps overhanging the angled edge, while the gap value sets the (smaller)
          distance between the icons and the feature block. Section height / clip are
          unchanged so the background image keeps its size and angles. */}
      <div
        ref={revealRef}
        className="relative z-10 flex min-h-[720px] md:min-h-[1100px] flex-col items-center justify-end gap-12 px-6 pt-44 pb-12 md:gap-16 md:pt-24 md:pb-20"
      >
        <div className="grid w-full max-w-6xl grid-cols-2 gap-x-8 gap-y-14 md:grid-cols-4 md:gap-12">
          {categories.map((category, index) => {
            const name = category.label;
            const badge = badgeForKey(category.key, index);
            const url = `/floor-plans?bedroom=${category.param}`;

            return (
              <a
                key={category.key}
                href={url}
                data-reveal
                style={{ transitionDelay: `${index * 100}ms` }}
                className="fp-reveal flex flex-col items-center text-center hover:scale-105"
              >
                <div className="relative w-32 md:w-44">
                  <FloorPlanIcon className="h-auto w-full text-white drop-shadow-md" />
                  <span
                    className="absolute -top-3 -right-2 flex h-12 w-12 items-center justify-center rounded-full bg-white text-2xl font-normal text-[var(--vibe-color-primary)] shadow-md md:h-16 md:w-16 md:text-4xl"
                    style={{ fontFamily: "'Oswald', 'Arial Narrow', system-ui, sans-serif" }}
                  >
                    {badge}
                  </span>
                </div>
                <span
                  className="mt-5 whitespace-nowrap text-sm font-normal uppercase tracking-wide text-[var(--vibe-color-secondary)] drop-shadow-sm md:text-xl"
                  style={{ fontFamily: "'Oswald', 'Arial Narrow', system-ui, sans-serif" }}
                >
                  {name}
                </span>
              </a>
            );
          })}
        </div>

        {/* Feature block: subtitle, paragraph, CTA + image. items-start top-aligns the
            text with the image so the taller image extends lower, past the angled edge. */}
        <div className="grid w-full max-w-6xl items-start gap-10 md:grid-cols-2 md:gap-16">
          {/* Text column */}
          <div className="flex flex-col items-start">
            <RichTextContent
              path="floorplanslist.feature_title"
              label="Feature Title"
              defaultValue="<p>Spacious Apartment Homes</p>"
              as="h2"
              className="text-3xl font-normal text-[var(--vibe-color-secondary)] md:text-[2.75rem]"
              style={{ fontFamily: "'Oswald', 'Arial Narrow', system-ui, sans-serif" }}
            />

            <RichTextContent
              path="floorplanslist.feature_body"
              label="Feature Body Text"
              defaultValue="<p>Discover the allure of our diverse floor plans, ranging from chic studios to expansive three-bedroom residences. Each layout is thoughtfully crafted to provide a harmonious blend of functionality and sophistication.</p>"
              as="div"
              className="fp-feature-body-font mt-8 max-w-xl text-base leading-relaxed text-white/90 md:text-lg"
            />

            <EditableSlotGroup groupPath="floorplanslist.feature_cta" definition={featureCtaDef}>
              <a
                href={featureCta.url || '/floorplans'}
                target={featureCta._target || '_self'}
                className="mt-10 inline-flex items-center justify-center bg-[var(--vibe-color-secondary)] px-10 py-4 text-sm font-normal uppercase tracking-widest text-[var(--vibe-color-text-on-secondary)] shadow-md transition-colors hover:bg-white hover:text-[var(--vibe-color-secondary)]"
                style={{ fontFamily: "'Oswald', 'Arial Narrow', system-ui, sans-serif" }}
              >
                {featureCta.label || 'Explore Now'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Image column — slides in from the right as it enters the viewport */}
          <div
            data-reveal
            className="fp-reveal-right relative aspect-[3/2] w-full overflow-hidden shadow-lg"
          >
            <EditableSlotGroup groupPath="floorplanslist.feature_image" definition={featureImageDef}>
              {featureImage.image ? (
                <VibesitesImage
                  url={featureImage.image}
                  alt={featureImage.alt || 'Property interior'}
                  sizes="(min-width: 768px) 50vw, 100vw"
                  className="absolute inset-0 h-full w-full object-cover object-center"
                />
              ) : (
                <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
              )}
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Scroll reveal: elements start shifted down + transparent, then slide
           up into place once the observer adds .is-visible. */
        .fp-reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
          will-change: opacity, transform;
        }
        .fp-reveal.is-visible {
          opacity: 1;
          transform: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-reveal {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
        /* Feature image reveal: starts shifted to the right + transparent, then
           slides left into place once the observer adds .is-visible. */
        .fp-reveal-right {
          opacity: 0;
          transform: translateX(60px);
          transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
          will-change: opacity, transform;
        }
        .fp-reveal-right.is-visible {
          opacity: 1;
          transform: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-reveal-right {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
        /* Match the PropertyOverview description typeface (Space Mono, loaded
           globally by the Header). The font-family inherits to the rich-text
           children so the feature body renders in the same face. */
        .fp-feature-body-font,
        .fp-feature-body-font * {
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        /* Full-section image clipped to angled edges: the top edge slopes down
           from left to right, and the bottom edge slopes down from right to
           left (the two angles run in opposite directions). */
        .fp-section-image {
          clip-path: polygon(0 0, 100% 14%, 100% 86%, 0 100%);
          -webkit-clip-path: polygon(0 0, 100% 14%, 100% 86%, 0 100%);
        }
        @media (min-width: 768px) {
          .fp-section-image {
            clip-path: polygon(0 0, 100% 18%, 100% 82%, 0 100%);
            -webkit-clip-path: polygon(0 0, 100% 18%, 100% 82%, 0 100%);
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.section_image': {
      label: 'Section Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    'floorplanslist.feature_title': {
      label: 'Feature Title',
      type: 'richtext',
      defaultValue: '<p>Spacious Apartment Homes</p>',
    },
    'floorplanslist.feature_body': {
      label: 'Feature Body Text',
      type: 'richtext',
      defaultField: 'property_home_floorplans_blurb',
      defaultValue:
        '<p>Discover the allure of our diverse floor plans, ranging from chic studios to expansive three-bedroom residences. Each layout is thoughtfully crafted to provide a harmonious blend of functionality and sophistication.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.feature_image': featureImageDef,
    'floorplanslist.feature_cta': featureCtaDef,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
